import * as React from 'react';
import styled from '@emotion/styled';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { Page, TLCode } from '../../common/enums';
import { Button, ButtonGroup, Classes, NumericInput, Tag } from '@blueprintjs/core';

import { IconNames } from '@blueprintjs/icons';
import moment from 'moment';
import TranslatedMessage from '../common/translation/TranslatedMessage';
import { Link } from 'react-router-dom';
import { PharmacyDetails } from '../../store/models/PharmacyDetails';
import { Instance } from 'mobx-state-tree';
import { Pharmacy } from '../../store/models/Pharmacy';
import DateInput from '../common/input/DateInput';
import { getFirstMonday, getFirstSunday } from '../../common/utils';

const FormContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const FormActionContainer = styled.div`
  margin-top: 5px;
  height: 100%;
  display: flex;
  flex-direction: row;
  margin-bottom: 15px;
  & * {
    margin-right: 5px;
  }
  & .${Classes.ICON} {
    margin: 0;
    & * {
      margin: 0;
    }
  }
`;

const FormFieldContainer = styled.div`
  height: 100%;
  display: inline-flex;
  > * {
    margin-right: 5px;
  }
  & .${Classes.FORM_GROUP} {
    margin-bottom: 0;
  }
  align-items: center;
`;

const DivLeft = styled.div`
  display: flex;
  flex-direction: row;
  width: 65%;
  margin-right: 5%;
  padding-bottom: 5px;
`;

const DivRight = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 5px;
  .bp3-icon {
    margin-right: 3px;
  }
`;

const FormAction = (props: { text: string; onClick: () => void }) => (
  <Tag {...props} round={true} interactive={true}>
    {props.text}
  </Tag>
);

interface DutiesSearchFormProps extends WithTranslateProps {
  formSubmitted: (form: any) => void;
  onStartDateChange: (date?: string) => void;
  onEndDateChange: (date?: string) => void;
  startDate?: string;
  endDate?: string;
  getPharmacyDetails: (id: number) => any;
  getPharmacyById: (id: number) => any;
  pharmacyList: Array<Instance<typeof PharmacyDetails>>;
}

interface DutiesSearchFormState {
  pharmacySelected?: Instance<typeof PharmacyDetails>;
  semesterYear: number;
}

class DutiesSearchForm extends React.PureComponent<DutiesSearchFormProps, DutiesSearchFormState> {
  state = {
    pharmacyList: [],
    semesterYear: new Date().getFullYear(),
  } as DutiesSearchFormState;

  setToday = (args, state, utils) => {
    const today = moment().toDate();
    utils.changeValue(state, 'startDate', () => today);
    utils.changeValue(state, 'endDate', () => today);
  };

  onPharmacyChange = async (pharmacy: Instance<typeof Pharmacy>) => {
    if (pharmacy.pharmacyId != null) {
      const details = await this.props.getPharmacyDetails(pharmacy.pharmacyId);
      this.setState({ pharmacySelected: details });
    } else {
      this.setState({ pharmacySelected: undefined });
    }
  };

  search = () => {
    const { startDate, endDate, formSubmitted } = this.props;
    formSubmitted({ startDate, endDate, notAssigned: false });
  };

  setS1 = () => {
    let date = getFirstMonday(this.state.semesterYear, 0);
    this.props.onStartDateChange(moment(date).format());

    date = getFirstSunday(this.state.semesterYear, 6);
    this.props.onEndDateChange(moment(date).format());
  };

  setS2 = () => {
    let date = getFirstMonday(this.state.semesterYear, 6);
    this.props.onStartDateChange(moment(date).format());

    date = getFirstSunday(this.state.semesterYear + 1, 0);
    this.props.onEndDateChange(moment(date).format());
  };

  render() {
    const { startDate, onStartDateChange, onEndDateChange } = this.props;

    const year = this.state.semesterYear;
    const addOneDay = (date) => moment(date).toDate();
    const addOneWeek = (date) => {
      return moment(date)
        .add(6, 'day')
        .toDate();
    };

    const addOneMonth = (date) =>
      moment(date)
        .add(1, 'month')
        .subtract(1, 'day')
        .toDate();
    const addOneYear = (date) =>
      moment(date)
        .add(1, 'year')
        .subtract(1, 'day')
        .toDate();
    //
    const initializeStart = () => {
      const date = this.props.startDate != null ? new Date(this.props.startDate) : undefined;
      if (date) {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0, 0);
      }
      return date;
    };

    const initializeEnd = () => {
      const date = this.props.endDate != null ? new Date(this.props.endDate) : undefined;
      if (date) {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0, 0);
      }
      return date;
    };

    return (
      <FormContainer>
        <FormFieldContainer>
          <DivLeft>
            <span style={{ marginRight: '5px' }}>
              <TranslatedMessage id={TLCode.DutiesBetweenStart} />
            </span>
            <DateInput
              value={initializeStart()}
              onChange={(date) => onStartDateChange(date ? moment(date).format() : undefined)}
            />
            <span style={{ marginLeft: '5px', marginRight: '5px' }}>
              <TranslatedMessage id={TLCode.DutiesBetweenEnd} />
            </span>
            <DateInput
              value={initializeEnd()}
              onChange={(date) => onEndDateChange(date ? moment(date).format() : undefined)}
            />
            <Button
              style={{ marginLeft: '15px', marginRight: '5px' }}
              minimal={true}
              icon={IconNames.ERASER}
              onClick={() => {
                this.props.onStartDateChange(undefined);
                this.props.onEndDateChange(undefined);
              }}
            />
            <Button intent="primary" icon={IconNames.SEARCH} onClick={() => this.search()}>
              <TranslatedMessage id={TLCode.GENERAL_Search} />
            </Button>
          </DivLeft>
          <DivRight>
            <Link to={Page.DutyAssignment + '/-1'}>
              <Button intent="primary">
                <TranslatedMessage id={TLCode.QUICK_DUTY_PAGE} />
              </Button>
            </Link>
          </DivRight>
        </FormFieldContainer>
        <FormActionContainer>
          <FormAction
            text={this.props.translate(TLCode.OneDay)}
            onClick={() => {
              const date = addOneDay(startDate);
              onEndDateChange(date ? moment(date).format() : undefined);
            }}
          />
          <FormAction
            text={this.props.translate(TLCode.OneWeek)}
            onClick={() => {
              const date = addOneWeek(startDate);
              onEndDateChange(date ? moment(date).format() : undefined);
            }}
          />
          <FormAction
            text={this.props.translate(TLCode.OneMonth)}
            onClick={() => {
              const date = addOneMonth(startDate);
              onEndDateChange(date ? moment(date).format() : undefined);
            }}
          />
          <FormAction
            text={this.props.translate(TLCode.OneYear)}
            onClick={() => {
              const date = addOneYear(startDate);
              onEndDateChange(date ? moment(date).format() : undefined);
            }}
          />
          <NumericInput
            style={{ width: '60px' }}
            min={2016}
            value={year}
            onValueChange={(nbr, str) => this.setState({ semesterYear: nbr })}
          />
          <ButtonGroup>
            <Button intent="primary" onClick={this.setS1}>
              <TranslatedMessage id={TLCode.ROSTER_S1} />
            </Button>
            &nbsp;&nbsp;
            <Button intent="primary" onClick={this.setS2}>
              <TranslatedMessage id={TLCode.ROSTER_S2} />
            </Button>
          </ButtonGroup>
        </FormActionContainer>
      </FormContainer>
    );
  }
}

export default withTranslate(DutiesSearchForm);
